import {
  IMOVEL_FINALIDADE_LABELS,
  IMOVEL_LISTING_STATUS_LABELS,
  IMOVEL_STATUS_CLASSES,
  IMOVEL_STATUS_LABELS,
  IMOVEL_TIPO_LABELS,
  PRICE_ROW_KINDS,
  RENTAL_ADMINISTRATOR_TO_GUARANTEE_TYPE,
  RENTAL_GUARANTEE_LABELS,
  RENTAL_GUARANTEE_TYPES,
  UNIT_AMENITY_TYPES,
} from 'dashboard/constants/lais/imovel';
import { HOUSE_CONTACT_ROLES } from 'dashboard/constants/lais/visita';
import { frontendURL } from 'dashboard/helper/URLHelper';
import { isPurchaseOfferContext } from './offerPresenter';
import { fallbackLabel, numberValue, toText } from './primitiveHelpers';

export const TAX_FEE_GOVERNANCE_KEY = 'tax_fee_governance';

export const TAX_FEE_FIELDS = Object.freeze([
  {
    field: 'valorCondominio',
    snakeField: 'valor_condominio',
    kind: PRICE_ROW_KINDS.CONDO_PRICE,
    label: 'Condomínio',
    emptyLabel: 'Condomínio sem origem',
  },
  {
    field: 'valorIptu',
    snakeField: 'valor_iptu',
    kind: PRICE_ROW_KINDS.PROPERTY_TAX,
    label: 'IPTU',
    emptyLabel: 'IPTU sem origem',
  },
]);

export const TAX_FEE_SOURCE_LABELS = Object.freeze({
  manual: 'Manual',
  imported: 'Importado',
  portal: 'Importado',
  estimated: 'Estimado',
  confirmed: 'Confirmado',
  unknown: 'Origem desconhecida',
});

export const TAX_FEE_CONFIDENCE_LABELS = Object.freeze({
  high: 'Alta confiança',
  medium: 'Confiança média',
  low: 'Baixa confiança',
  confirmed: 'Confirmado',
  unknown: 'Confiança desconhecida',
});

const snakeKey = key =>
  String(key).replace(/[A-Z]/g, char => `_${char.toLowerCase()}`);

const objectValue = (source, key) => {
  if (!source) return undefined;
  const snake = snakeKey(key);
  return source[key] ?? source[snake];
};

const nestedValue = (source, key) => {
  const direct = objectValue(source, key);
  if (direct !== undefined && direct !== null && direct !== '') return direct;

  const attrs =
    source?.additionalAttributes || source?.additional_attributes || {};
  const attr = objectValue(attrs, key);
  if (attr !== undefined && attr !== null && attr !== '') return attr;

  const characteristics =
    source?.caracteristicas || source?.characteristics || {};
  return objectValue(characteristics, key);
};

const booleanValue = value => {
  if (value === true || value === false) return value;
  if (['true', 'sim', 'yes', '1'].includes(String(value).toLowerCase())) {
    return true;
  }
  if (
    ['false', 'nao', 'não', 'no', '0'].includes(String(value).toLowerCase())
  ) {
    return false;
  }
  return null;
};

const plural = (value, singular, pluralLabel) => {
  const amount = numberValue(value);
  if (amount === null) return '-';
  return `${amount} ${amount === 1 ? singular : pluralLabel}`;
};

const formatMeters = value => {
  const amount = numberValue(value);
  return amount === null ? '-' : `${amount} m²`;
};

const formatYesNo = value => (booleanValue(value) ? 'Sim' : 'Não');

const firstPresentObjectValue = (source, keys) => {
  if (!source) return undefined;

  return keys
    .map(key => objectValue(source, key))
    .find(value => value !== undefined && value !== null && value !== '');
};

const governancePayload = imovel => {
  const attrs =
    imovel?.additionalAttributes || imovel?.additional_attributes || {};
  return (
    objectValue(imovel, 'taxFeeGovernance') ||
    objectValue(attrs, 'taxFeeGovernance') ||
    objectValue(attrs, 'feeGovernance') ||
    {}
  );
};

const governanceEntryFor = (imovel, config) => {
  const governance = governancePayload(imovel);
  return (
    governance?.[config.snakeField] ||
    governance?.[config.field] ||
    governance?.[config.kind] ||
    {}
  );
};

const feeHistory = entry => {
  const history =
    objectValue(entry, 'history') || objectValue(entry, 'events') || [];
  return Array.isArray(history) ? history : [];
};

const feeCanonicalValue = (imovel, config, fallback) => {
  const entry = governanceEntryFor(imovel, config);
  const canonicalValue = numberValue(
    firstPresentObjectValue(entry, ['canonicalValue', 'value'])
  );
  return canonicalValue === null ? fallback : canonicalValue;
};

export const buildTaxFeeGovernanceRows = imovel =>
  TAX_FEE_FIELDS.map(config => {
    const entry = governanceEntryFor(imovel, config);
    const storedValue = numberValue(nestedValue(imovel, config.field));
    const canonicalValue = feeCanonicalValue(imovel, config, storedValue);
    const fallbackValue = numberValue(
      firstPresentObjectValue(entry, ['fallbackValue', 'importedValue'])
    );
    const sourceKind =
      firstPresentObjectValue(entry, ['canonicalSource', 'sourceKind']) ||
      'unknown';
    const sourceLabel =
      firstPresentObjectValue(entry, ['sourceLabel', 'origin']) ||
      TAX_FEE_SOURCE_LABELS[sourceKind] ||
      fallbackLabel(sourceKind);
    const status =
      firstPresentObjectValue(entry, ['status', 'sourceStatus']) || sourceKind;
    const confidence =
      firstPresentObjectValue(entry, [
        'confidence',
        'canonicalConfidence',
        'confidenceLevel',
      ]) || 'unknown';
    const originDate = firstPresentObjectValue(entry, [
      'originDate',
      'observedAt',
      'canonicalUpdatedAt',
      'updatedAt',
    ]);
    const conflict =
      Boolean(objectValue(entry, 'conflict')) ||
      (fallbackValue !== null &&
        canonicalValue !== null &&
        fallbackValue !== canonicalValue);

    return {
      field: config.snakeField,
      kind: config.kind,
      label: config.label,
      emptyLabel: config.emptyLabel,
      value: storedValue,
      canonicalValue,
      fallbackValue,
      fallbackSource:
        firstPresentObjectValue(entry, ['fallbackSource', 'importedSource']) ||
        '',
      sourceKind,
      sourceLabel,
      status,
      statusLabel: TAX_FEE_SOURCE_LABELS[status] || fallbackLabel(status),
      confidence,
      confidenceLabel:
        TAX_FEE_CONFIDENCE_LABELS[confidence] || fallbackLabel(confidence),
      originDate,
      conflict,
      history: feeHistory(entry),
      traceLabel: [
        config.label,
        sourceLabel,
        TAX_FEE_CONFIDENCE_LABELS[confidence] || fallbackLabel(confidence),
      ]
        .filter(Boolean)
        .join(' · '),
    };
  });

export const getImovelStatusDescriptor = status => ({
  status: status || '',
  label: IMOVEL_STATUS_LABELS[status] || fallbackLabel(status),
  className:
    IMOVEL_STATUS_CLASSES[status] || 'text-lais-text-body bg-lais-bg-soft',
});

export const getImovelStatusLabel = status =>
  getImovelStatusDescriptor(status).label;

export const getImovelStatusClass = status =>
  getImovelStatusDescriptor(status).className;

export const getImovelTipoLabel = tipo =>
  IMOVEL_TIPO_LABELS[tipo] || fallbackLabel(tipo);

export const getImovelFinalidadeLabel = finalidade =>
  IMOVEL_FINALIDADE_LABELS[finalidade] || fallbackLabel(finalidade);

export const getImovelListingStatusLabel = listingStatus =>
  IMOVEL_LISTING_STATUS_LABELS[listingStatus] || fallbackLabel(listingStatus);

export const getHouseDeepLink = ({
  accountId,
  businessContext,
  imovelId,
  origin = '',
} = {}) => {
  if (!accountId || !imovelId) return '';

  const params = new URLSearchParams();
  params.set(
    'context',
    isPurchaseOfferContext(businessContext) ? 'comprar' : 'visitar'
  );
  return `${origin}${frontendURL(`accounts/${accountId}/imoveis/${imovelId}`)}?${params.toString()}`;
};

export const imovelAddressLine = imovel =>
  [
    imovel?.titulo || imovel?.endereco || imovel?.address,
    imovel?.numero || imovel?.number,
    imovel?.complemento || imovel?.complement,
  ]
    .filter(Boolean)
    .join(', ');

export const buildSuggestHouseMessage = ({
  accountId,
  businessContext,
  contact,
  imovel,
  origin = '',
} = {}) => {
  const name = contact?.name || contact?.nome || 'cliente';
  const address = imovelAddressLine(imovel) || 'este imóvel';
  const url = getHouseDeepLink({
    accountId,
    businessContext,
    imovelId: imovel?.id,
    origin,
  });

  return [
    `Olá ${name},`,
    'Encontrei um novo imóvel com base nos seus interesses, o que acha de visitarmos?',
    address,
    url,
  ].join('\n');
};

const amenityValue = (imovel, ...keys) => {
  let result = null;
  keys.some(key => {
    const value = nestedValue(imovel, key);
    if (value !== undefined && value !== null && value !== '') {
      result = value;
      return true;
    }
    return false;
  });
  return result;
};

export const getUnitAmenities = imovel => {
  const area = amenityValue(imovel, 'areaUtil', 'areaTotal', 'area');
  const bedrooms = amenityValue(imovel, 'quartos', 'bedrooms');
  const suites = numberValue(amenityValue(imovel, 'suites'));
  const bathrooms = amenityValue(imovel, 'banheiros', 'bathrooms');
  const parking = amenityValue(imovel, 'vagasGaragem', 'parkingSpots');
  const floor = amenityValue(imovel, 'andar', 'floor');
  const pets = amenityValue(imovel, 'aceitaPets', 'petFriendly', 'pets');
  const furniture = amenityValue(imovel, 'mobiliado', 'furnished');
  const subway = amenityValue(
    imovel,
    'pertoMetro',
    'metroProximo',
    'nearSubway',
    'subway'
  );

  return [
    {
      type: UNIT_AMENITY_TYPES.AREA,
      icon: 'i-woot-lais-amenity-area',
      title: formatMeters(area),
      description: 'Área',
      disabled: false,
    },
    {
      type: UNIT_AMENITY_TYPES.BEDROOM,
      icon: 'i-woot-lais-amenity-bedroom',
      title: plural(bedrooms, 'quarto', 'quartos'),
      description: suites > 0 ? plural(suites, 'suíte', 'suítes') : '',
      disabled: false,
    },
    {
      type: UNIT_AMENITY_TYPES.BATHROOM,
      icon: 'i-woot-lais-amenity-bathroom',
      title: plural(bathrooms, 'banheiro', 'banheiros'),
      description: 'Banheiros',
      disabled: !numberValue(bathrooms),
    },
    {
      type: UNIT_AMENITY_TYPES.PARKING,
      icon: 'i-woot-lais-amenity-parking',
      title: plural(parking, 'vaga', 'vagas'),
      description: 'Garagem',
      disabled: !numberValue(parking),
    },
    {
      type: UNIT_AMENITY_TYPES.FLOOR,
      icon: 'i-woot-lais-amenity-building',
      title: floor ? `${floor}º andar` : '-',
      description: 'Andar',
      disabled: false,
    },
    {
      type: UNIT_AMENITY_TYPES.PETS,
      icon: 'i-woot-lais-amenity-pet',
      title: formatYesNo(pets),
      description: 'Aceita pet',
      disabled: booleanValue(pets) !== true,
    },
    {
      type: UNIT_AMENITY_TYPES.FURNITURE,
      icon: 'i-woot-lais-amenity-furnished',
      title: formatYesNo(furniture),
      description: 'Mobiliado',
      disabled: booleanValue(furniture) !== true,
    },
    {
      type: UNIT_AMENITY_TYPES.SUBWAY,
      icon: 'i-woot-lais-amenity-public-transport',
      title: formatYesNo(subway),
      description: 'Perto do metrô',
      disabled: booleanValue(subway) !== true,
    },
  ];
};

const priceDialogFor = ({ kind, companyName = 'Imudei' } = {}) => {
  const dialogs = {
    [PRICE_ROW_KINDS.CONDO_PRICE]: {
      title: 'Valor do condomínio',
      description:
        'Taxa mensal cobrada pelo condomínio. Confirme o valor antes de enviar proposta.',
      items: ['Pode variar por assembleia.', 'Não inclui consumo individual.'],
    },
    [PRICE_ROW_KINDS.PROPERTY_TAX]: {
      title: 'IPTU',
      description:
        'Imposto do imóvel rateado mensalmente na simulação de aluguel.',
      items: ['O valor anual pode mudar por atualização da prefeitura.'],
    },
    [PRICE_ROW_KINDS.HOME_PROTECTION]: {
      title: 'Seguro incêndio',
      description:
        'Estimativa mensal obrigatória em contratos de locação residencial.',
      items: [
        'A exibição arredonda o valor para cima.',
        'O valor final pode variar na contratação.',
      ],
    },
    [PRICE_ROW_KINDS.TENANT_SERVICE_FEE]: {
      title: 'Taxa de serviço',
      description: `Taxa cobrada pela ${companyName} para operar a locação.`,
      items: [
        'Percentual aplicado sobre o aluguel.',
        'Cobre operação, suporte e acompanhamento do contrato.',
      ],
    },
  };

  return dialogs[kind] || null;
};

const rentalGuaranteeDialog = type => ({
  title: RENTAL_GUARANTEE_LABELS[type] || 'Garantia de aluguel',
  description:
    type === RENTAL_GUARANTEE_TYPES.STANDALONE
      ? 'Garantia contratada de forma avulsa para reduzir risco de inadimplência.'
      : 'Garantia operada por parceiro ou administrador terceiro.',
  items:
    type === RENTAL_GUARANTEE_TYPES.STANDALONE
      ? [
          'Pode ter pagamento separado do aluguel.',
          'A renovação depende das regras da garantia contratada.',
          'O corretor deve confirmar disponibilidade antes da proposta.',
        ]
      : [
          'O parceiro define aprovação, cobrança e renovação.',
          'A cobrança pode ser mensal ou conforme contrato.',
          'Confirme as condições antes de orientar o cliente.',
        ],
});

export const rentalGuaranteeTypeFor = imovel => {
  const explicit =
    nestedValue(imovel, 'rentalGuarantee') ||
    nestedValue(imovel, 'garantiaAluguel') ||
    nestedValue(imovel, 'rentalGuaranteeType');
  if (Object.values(RENTAL_GUARANTEE_TYPES).includes(explicit)) {
    return explicit;
  }

  return (
    RENTAL_ADMINISTRATOR_TO_GUARANTEE_TYPE[
      nestedValue(imovel, 'rentalAdministrator')
    ] || null
  );
};

const fallbackPriceRows = imovel => {
  const rent = numberValue(nestedValue(imovel, 'valorAluguel'));
  const sale = numberValue(nestedValue(imovel, 'valorVenda'));
  const feeRows = buildTaxFeeGovernanceRows(imovel);
  const condoGovernance = feeRows.find(
    row => row.kind === PRICE_ROW_KINDS.CONDO_PRICE
  );
  const taxGovernance = feeRows.find(
    row => row.kind === PRICE_ROW_KINDS.PROPERTY_TAX
  );
  const condo = numberValue(condoGovernance?.canonicalValue);
  const tax = numberValue(taxGovernance?.canonicalValue);
  const finalidade = nestedValue(imovel, 'finalidade');
  const rows = [];

  if (finalidade === 'aluguel' && rent !== null) {
    rows.push({
      kind: PRICE_ROW_KINDS.RENT_PRICE,
      label: 'Aluguel',
      value: rent,
    });
  }
  if (finalidade !== 'aluguel' && sale !== null) {
    rows.push({
      kind: PRICE_ROW_KINDS.SALE_PRICE,
      label: 'Valor de venda',
      value: sale,
    });
  }
  if (condo !== null) {
    rows.push({
      kind: PRICE_ROW_KINDS.CONDO_PRICE,
      label: 'Condomínio',
      value: condo,
    });
  }
  if (tax !== null) {
    rows.push({
      kind: PRICE_ROW_KINDS.PROPERTY_TAX,
      label: finalidade === 'aluguel' ? 'IPTU mensal' : 'IPTU anual',
      value: finalidade === 'aluguel' ? tax / 12 : tax,
      annualValue: tax,
    });
  }

  return rows;
};

const canonicalPriceKind = kind => {
  const map = {
    RENT: PRICE_ROW_KINDS.RENT_PRICE,
    SALE: PRICE_ROW_KINDS.SALE_PRICE,
    CONDOMINIUM: PRICE_ROW_KINDS.CONDO_PRICE,
    HOUSE_TAX: PRICE_ROW_KINDS.PROPERTY_TAX,
  };
  return map[kind] || kind;
};

const enrichPriceRow = (row, options) => {
  const kind = canonicalPriceKind(row.kind);
  const value = numberValue(row.value);
  const normalizedValue =
    kind === PRICE_ROW_KINDS.HOME_PROTECTION && value !== null
      ? Math.ceil(value)
      : value;

  return {
    ...row,
    kind,
    value: normalizedValue,
    annualValue: numberValue(row.annualValue || row.annual_value),
    feeGovernance: options.feeGovernanceByKind?.[kind],
    dialog: row.dialog || priceDialogFor({ kind, ...options }),
  };
};

export const buildPriceBreakdownRows = (
  imovel,
  { rows, countryCode = 'BR', companyName = 'Imudei' } = {}
) => {
  const sourceRows =
    Array.isArray(rows) && rows.length ? rows : fallbackPriceRows(imovel);
  const feeGovernanceByKind = Object.fromEntries(
    buildTaxFeeGovernanceRows(imovel).map(row => [row.kind, row])
  );
  const regularRows = sourceRows
    .filter(row => row?.kind !== PRICE_ROW_KINDS.TOTAL_COST)
    .map(row => enrichPriceRow(row, { companyName, feeGovernanceByKind }));
  const finalidade = nestedValue(imovel, 'finalidade');
  const guaranteeType = rentalGuaranteeTypeFor(imovel);
  const shouldShowGuarantee =
    finalidade === 'aluguel' &&
    countryCode === 'BR' &&
    Object.values(RENTAL_GUARANTEE_TYPES).includes(guaranteeType) &&
    !regularRows.some(row => row.kind === PRICE_ROW_KINDS.RENTAL_GUARANTEE);

  if (shouldShowGuarantee) {
    regularRows.push({
      kind: PRICE_ROW_KINDS.RENTAL_GUARANTEE,
      label: 'Garantia de aluguel',
      value: null,
      valueLabel: RENTAL_GUARANTEE_LABELS[guaranteeType],
      dialog: rentalGuaranteeDialog(guaranteeType),
    });
  }

  const numericTotal = regularRows
    .filter(row => row.kind !== PRICE_ROW_KINDS.RENTAL_GUARANTEE)
    .reduce((sum, row) => sum + (numberValue(row.value) || 0), 0);
  const totalRow = sourceRows.find(
    row => row?.kind === PRICE_ROW_KINDS.TOTAL_COST
  );
  const total =
    totalRow && numberValue(totalRow.value) !== null
      ? numberValue(totalRow.value)
      : numericTotal || null;

  return [
    ...regularRows,
    ...(total !== null
      ? [
          {
            kind: PRICE_ROW_KINDS.TOTAL_COST,
            label:
              totalRow?.label ||
              (finalidade === 'aluguel'
                ? 'Custo total mensal'
                : 'Custo mensal estimado'),
            value: Math.ceil(total),
          },
        ]
      : []),
  ];
};

export const isThirdPartyListing = imovel =>
  nestedValue(imovel, 'listingSource') === 'third_party' ||
  nestedValue(imovel, 'isThirdParty') === true;

const roleValue = contact =>
  String(contact?.role || contact?.kind || contact?.type || '').toUpperCase();

const contactPhoneValue = contact =>
  toText(
    contact?.phone ||
      contact?.phoneNumber ||
      contact?.phone_number ||
      contact?.cellphone ||
      contact?.cellPhone ||
      contact?.cell_phone
  );

export const hasCiqContact = contacts =>
  (Array.isArray(contacts) ? contacts : []).some(
    contact => roleValue(contact) === HOUSE_CONTACT_ROLES.CIQ
  );

export const suppressLandlordPhoneWhenCiq = contacts => {
  const list = Array.isArray(contacts) ? contacts : [];
  const shouldSuppress = hasCiqContact(contacts);
  return list.map(contact => {
    if (shouldSuppress && roleValue(contact) === HOUSE_CONTACT_ROLES.LANDLORD) {
      return { ...contact, phone: '', cellphone: '', phoneNumber: '' };
    }
    return contact;
  });
};

export const hasValidListingRepresentative = contact =>
  roleValue(contact) === HOUSE_CONTACT_ROLES.LISTING_REPRESENTATIVE &&
  !!contactPhoneValue(contact);

const roleLabel = role =>
  ({
    [HOUSE_CONTACT_ROLES.LANDLORD]: 'Proprietário',
    [HOUSE_CONTACT_ROLES.TENANT]: 'Inquilino',
    [HOUSE_CONTACT_ROLES.CIQ]: 'Consultor do imóvel',
    [HOUSE_CONTACT_ROLES.LISTING_REPRESENTATIVE]: 'Representante do anúncio',
    [HOUSE_CONTACT_ROLES.CLIENT]: 'Cliente',
  })[role] || 'Contato';

const normalizeHouseContact = contact => {
  const role = roleValue(contact) || HOUSE_CONTACT_ROLES.CLIENT;
  return {
    id:
      contact?.id ||
      `${role}-${contact?.name || contactPhoneValue(contact) || 'contact'}`,
    role,
    roleLabel: roleLabel(role),
    name:
      contact?.name || contact?.nome || contact?.displayName || roleLabel(role),
    phone: contactPhoneValue(contact),
    email: contact?.email || '',
    source: contact?.source || '',
    canSendOffer: role === HOUSE_CONTACT_ROLES.TENANT,
    isListingRepresentative: hasValidListingRepresentative(contact),
  };
};

const inferredContacts = ({ visit, imovel }) => {
  const attrs =
    visit?.additionalAttributes ||
    visit?.additional_attributes ||
    imovel?.additionalAttributes ||
    imovel?.additional_attributes ||
    {};
  return [
    visit?.contact
      ? { ...visit.contact, role: HOUSE_CONTACT_ROLES.CLIENT }
      : null,
    attrs.ownerContact || attrs.owner_contact
      ? {
          ...(attrs.ownerContact || attrs.owner_contact),
          role: HOUSE_CONTACT_ROLES.LANDLORD,
        }
      : null,
    attrs.tenantContact || attrs.tenant_contact
      ? {
          ...(attrs.tenantContact || attrs.tenant_contact),
          role: HOUSE_CONTACT_ROLES.TENANT,
        }
      : null,
    attrs.listingRepresentative || attrs.listing_representative
      ? {
          ...(attrs.listingRepresentative || attrs.listing_representative),
          role: HOUSE_CONTACT_ROLES.LISTING_REPRESENTATIVE,
        }
      : null,
  ].filter(Boolean);
};

export const normalizeHouseContacts = ({ contacts, visit, imovel } = {}) => {
  const attrs =
    visit?.additionalAttributes || visit?.additional_attributes || {};
  const rawContacts =
    contacts ||
    attrs.houseContacts ||
    attrs.house_contacts ||
    inferredContacts({ visit, imovel });
  const list = Array.isArray(rawContacts) ? rawContacts : [];
  const scopedContacts = isThirdPartyListing(imovel)
    ? list.filter(
        contact => roleValue(contact) !== HOUSE_CONTACT_ROLES.LANDLORD
      )
    : list;

  return suppressLandlordPhoneWhenCiq(scopedContacts).map(
    normalizeHouseContact
  );
};

const mediaUrl = media => {
  if (!media) return '';
  if (typeof media === 'string') return media;
  return media.url || media.src || media.thumbnail || '';
};

export const normalizeSimilarListing = (
  item,
  { accountId, origin = '' } = {}
) => {
  const finalidade = item?.finalidade || item?.businessContext || 'aluguel';
  const price =
    finalidade === 'aluguel'
      ? numberValue(item?.valorAluguel || item?.rentPrice)
      : numberValue(item?.valorVenda || item?.salePrice);
  const context = finalidade === 'venda' ? 'comprar' : 'visitar';
  const id = item?.id || item?.imovelId;
  const thumbnail = mediaUrl(
    item?.thumbnail || item?.midias?.[0] || item?.media?.[0]
  );

  return {
    id,
    title: item?.titulo || item?.endereco || 'Imóvel semelhante',
    subtitle: [item?.bairro, item?.cidade].filter(Boolean).join(', '),
    details: [
      item?.areaUtil ? `${item.areaUtil} m²` : null,
      item?.quartos ? plural(item.quartos, 'quarto', 'quartos') : null,
    ].filter(Boolean),
    thumbnail,
    price,
    finalidade,
    distanceLabel:
      item?.distance || item?.distancia
        ? `${Number(item.distance || item.distancia).toFixed(0)} m`
        : '',
    to:
      accountId && id
        ? `${origin}${frontendURL(`accounts/${accountId}/imoveis/${id}`)}?context=${context}`
        : item?.linkExterno || item?.link_externo || '',
  };
};

export const normalizeSimilarListingsResponse = (payload, options = {}) => {
  const items = payload?.data || payload?.items || payload?.payload?.data || [];
  return {
    recommendationId:
      payload?.recommendation_id ||
      payload?.recommendationId ||
      payload?.payload?.recommendationId ||
      '',
    recommendationType:
      payload?.recommendation_type ||
      payload?.recommendationType ||
      payload?.payload?.recommendationType ||
      '',
    items: items.map(item => normalizeSimilarListing(item, options)),
  };
};

export default getImovelStatusDescriptor;
